import React from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, LayoutTemplate, Palette, ShoppingBag, Globe, Rocket, Sparkles, Check } from "lucide-react";

// Phase 82 — guide pas-à-pas + choix du modèle
const STEPS = [
  { key: "template", icon: LayoutTemplate, title: "1. Choisir un modèle", text: "Partez d'un modèle prêt (services, boutique, association…). Vous pourrez tout modifier ensuite.", tab: "template" },
  { key: "content", icon: Sparkles, title: "2. Personnaliser le contenu", text: "Activez / réordonnez les sections, écrivez vos textes, ajoutez vos photos. Chaque section a son aide.", tab: "content" },
  { key: "design", icon: Palette, title: "3. Apparence & identité", text: "Couleurs, police, boutons, logo, nom du site, réseaux sociaux et référencement (SEO).", tab: "design" },
  { key: "shop", icon: ShoppingBag, title: "4. Boutique (optionnel)", text: "Ajoutez vos produits, choisissez retrait / livraison et vos moyens de paiement (Stripe, PayPal, sur place).", tab: "shop" },
  { key: "domain", icon: Globe, title: "5. Adresse web", text: "Choisissez votre nom (adresse PAG nom.avenuerh.pro) ou rattachez votre propre nom de domaine.", tab: "domain" },
  { key: "publish", icon: Rocket, title: "6. Publier", text: "Prévisualisez puis publiez : votre site est en ligne immédiatement.", tab: "publish" },
];

export function GuidePanel({ done = [], onGo, onToggle }) {
  const pct = Math.round((done.length / STEPS.length) * 100);
  return (
    <div className="bg-white border border-[#DEE2E6] p-5" data-testid="guide-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-bold text-lg">Guide de création</div>
        <div className="text-xs font-mono text-[#495057]" data-testid="guide-progress">{done.length}/{STEPS.length} étapes · {pct} %</div>
      </div>
      <div className="h-1.5 bg-[#F1F3F5] mb-4"><div className="h-full bg-[#059669] transition-all" style={{ width: `${pct}%` }} /></div>
      <div className="space-y-2">
        {STEPS.map((s) => {
          const ok = done.includes(s.key);
          return (
            <div key={s.key} className={`flex items-start gap-3 p-3 border ${ok ? "border-[#059669]/40 bg-[#ECFDF5]" : "border-[#DEE2E6]"}`} data-testid={`guide-step-${s.key}`}>
              <button type="button" onClick={() => onToggle(s.key)} className="mt-0.5" aria-label="Marquer l'étape" data-testid={`guide-check-${s.key}`}>{ok ? <CheckCircle2 className="h-5 w-5 text-[#059669]" /> : <Circle className="h-5 w-5 text-[#DEE2E6]" />}</button>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold flex items-center gap-2"><s.icon className="h-3.5 w-3.5 text-[#002FA7]" /> {s.title}</div>
                <div className="text-xs text-[#495057] mt-0.5">{s.text}</div>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={() => onGo(s.tab)} className="h-7 text-xs shrink-0" data-testid={`guide-go-${s.key}`}>Ouvrir</Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function TemplatePicker({ templates, current, onApply, busy }) {
  return (
    <div className="bg-white border border-[#DEE2E6] p-5" data-testid="template-picker">
      <div className="font-display font-bold text-lg">Modèles prêts à l'emploi</div>
      <p className="text-xs text-[#495057] mt-1 mb-4">Appliquer un modèle remplace les sections par un contenu d'exemple adapté (vos textes actuels seront remplacés — votre nom, logo, adresse web et produits sont conservés).</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {templates.map((t) => (
          <div key={t.key} className={`border flex flex-col overflow-hidden transition-shadow hover:shadow-lg ${current === t.key ? "border-[#002FA7] ring-2 ring-[#002FA7]/20" : "border-[#DEE2E6]"}`} data-testid={`template-${t.key}`}>
            <div className="relative aspect-[16/9] bg-[#0A0A0A] overflow-hidden" data-testid={`template-thumb-${t.key}`}>
              {t.image && <img src={t.image} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-80" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute left-3 bottom-3 right-3 flex items-end justify-between gap-2">
                <div className="text-white">
                  <div className="text-[9px] uppercase tracking-[0.25em] font-bold opacity-80">{t.multi ? `${t.pages?.length || 0} onglets` : "Une page"}</div>
                  <div className="font-display font-black text-base leading-tight drop-shadow">{t.label}</div>
                </div>
                <div className="flex gap-1 shrink-0"><span className="h-4 w-4 border border-white/60" style={{ background: t.theme?.primary }} /><span className="h-4 w-4 border border-white/60" style={{ background: t.theme?.accent }} /></div>
              </div>
              <div className="absolute top-2 right-2 flex gap-1">
                {t.multi && <span className="text-[9px] uppercase tracking-wider font-bold bg-[#002FA7] text-white px-1.5 py-0.5" data-testid={`template-multi-${t.key}`}>Onglets</span>}
                {t.shop && <span className="text-[9px] uppercase tracking-wider font-bold bg-[#FFD700] text-[#0A0A0A] px-1.5 py-0.5">Boutique</span>}
              </div>
            </div>
            <div className="p-4 flex flex-col flex-1">
              <div className="text-xs text-[#495057] flex-1">{t.description}</div>
              {t.multi && !!t.pages?.length && <div className="mt-2 flex flex-wrap gap-1">{t.pages.map((p) => <span key={p} className="text-[9px] px-1.5 py-0.5 bg-[#F1F3F5] border border-[#DEE2E6]">{p}</span>)}</div>}
              {t.products > 0 && <div className="mt-2 text-[10px] text-[#495057]">+ {t.products} produits d'exemple (brouillons à activer)</div>}
              <Button type="button" size="sm" disabled={busy} onClick={() => { if (current !== t.key || window.confirm("Réappliquer ce modèle et remplacer le contenu actuel ?")) onApply(t.key); }}
                      className={`mt-3 h-8 text-xs ${current === t.key ? "bg-[#059669]" : "bg-[#002FA7]"} text-white`} data-testid={`template-apply-${t.key}`}>
                {current === t.key ? <><Check className="h-3 w-3 mr-1" /> Modèle actuel</> : "Utiliser ce modèle"}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
